import React, { useState, useEffect, useRef } from 'react';
import { isMacAppStore } from '@/lib/appDistribution';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Link, usePathname, useRouter } from 'expo-router';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import Tooltip from '@/components/Tooltip';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthStore, useModalStore, useOnboardingStore } from '@/store';
import { registerAnchor, unregisterAnchor } from '@/hooks/useTooltipAnchor';
import { AGENT_ICON, ARTIFACT_NAV_RAIL_WIDTH } from '@/constants/config';
import { formatCreditBalance, wholeCredits } from '@/lib/formatCredits';
import SidebarAccountButton from './SidebarAccountButton';
import useRequireAuth from '@/hooks/useRequireAuth';

const MoreTrigger = Platform.OS === 'web' ? View : Pressable;
const compactCredits = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });
const MORE_NATIVE_ICONS = {
  'mobile-screen': ['iphone', 'ic_menu_call'],
  laptop: ['laptopcomputer', 'ic_menu_view'],
  'clipboard-list': ['checklist', 'ic_menu_agenda'],
  cube: ['cube', 'ic_menu_manage'],
  bookmark: ['bookmark', 'ic_menu_save'],
  compass: ['safari', 'ic_menu_compass'],
  'layer-group': ['square.stack', 'ic_menu_edit'],
  image: ['photo', 'ic_menu_gallery'],
  video: ['video', 'ic_menu_slideshow'],
  music: ['music.note', 'ic_media_play'],
};

export function RailDestination({ label, icon, href, active, onPress, colors }) {
  const [highlighted, setHighlighted] = useState(false);
  const anchor = useRef(null);
  const onboarding = useOnboardingStore(state => state.actions);
  useEffect(() => {
    if (!href) return undefined;
    const id = `${href.slice(1)}-nav`;
    registerAnchor(id, anchor);
    if (href === '/files') onboarding.registerTarget(4, anchor);
    return () => {
      unregisterAnchor(id);
      if (href === '/files') onboarding.unregisterTarget(4);
    };
  }, [href, onboarding]);
  const button = (
    <Pressable ref={anchor} accessibilityRole={href ? 'link' : 'button'} accessibilityLabel={label}
      accessibilityState={{ selected: !!active }}
      {...(Platform.OS === 'web' && active ? { 'aria-current': 'page' } : {})}
      onPress={onPress} onHoverIn={() => setHighlighted(true)} onHoverOut={() => setHighlighted(false)}
      onFocus={() => setHighlighted(true)} onBlur={() => setHighlighted(false)}
      style={StyleSheet.flatten([styles.button, (active || highlighted) && { backgroundColor: colors.solid_color_secondary }])}>
      <Icon name={icon} size={21} color={active ? colors.text_color_primary : colors.text_color_secondary} />
    </Pressable>
  );
  return <Tooltip label={label} side="right">
    {href ? <Link href={href} asChild withAnchor>{button}</Link> : button}
  </Tooltip>;
}

function RailCreditBalance({ colors, onPress }) {
  const { t } = useTranslation();
  const balance = useAuthStore(state => state.user?.credits?.totalAvailable);
  const hasLoaded = useAuthStore(state => state.hasLoadedServerUser);
  const [highlighted, setHighlighted] = useState(false);
  const hasBalance = hasLoaded && balance != null && Number.isFinite(Number(balance));
  const label = `${t('settingsWeb.credits')}: ${hasBalance ? formatCreditBalance(balance) : '—'}`;

  return (
    <Tooltip label={label} side="right">
      <Link href="/settings?tab=credits" asChild withAnchor>
        <Pressable accessibilityRole="link" accessibilityLabel={label} onPress={onPress}
          onHoverIn={() => setHighlighted(true)} onHoverOut={() => setHighlighted(false)}
          onFocus={() => setHighlighted(true)} onBlur={() => setHighlighted(false)}
          style={StyleSheet.flatten([styles.creditButton, highlighted && { backgroundColor: colors.solid_color_secondary }])}>
          <Text size="xs" color={highlighted ? 'secondary' : 'tertiary'} numberOfLines={1} adjustsFontSizeToFit
            style={styles.creditAmount}>
            {hasBalance ? compactCredits.format(wholeCredits(balance)) : '—'}
          </Text>
          <Text size="xxs" color={highlighted ? 'secondary' : 'tertiary'} numberOfLines={1} adjustsFontSizeToFit>
            {t('settingsWeb.credits')}
          </Text>
        </Pressable>
      </Link>
    </Tooltip>
  );
}

/** Shared destinations for the web sidebar and native drawers. */
export default function MainNavigationRail({ navigation, onNavigate, header, children }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const pathname = usePathname();
  const router = useRouter();
  const requireAuth = useRequireAuth();
  const hasUser = useAuthStore(state => !!state.user?.id);
  const hasUsedMac = useAuthStore(state => state.user?.tracking?.hasUsedMacApp === true);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost === true);
  const openModal = useModalStore(state => state.actions.openModal);
  const showRemote = hasUser && !isGhost && Platform.OS !== 'web' && !isMacAppStore();
  const remote = { href: '/remote-projects', label: 'Remote', icon: Platform.OS === 'macos' ? 'mobile-screen' : 'laptop' };
  const primary = [
    { href: '/app', label: t('navigation.home'), icon: 'home' },
    { href: '/files', label: t('navigation.files'), icon: 'files' },
    { href: '/agents', label: t('navigation.agents'), icon: AGENT_ICON },
    { href: '/ideas', label: 'Ideas', icon: 'lightbulb' },
    { href: '/maps', label: 'Maps', icon: 'map' },
    { href: '/tools', label: t('navigation.tools'), icon: 'wand-magic-sparkles' },
    ...(showRemote && (hasUsedMac || Platform.OS === 'macos') ? [remote] : []),
  ];
  const more = [
    ...(showRemote && !hasUsedMac && Platform.OS !== 'macos' ? [remote] : []),
    { href: '/tasks', label: t('navigation.tasks'), icon: 'clipboard-list' },
    { href: '/models', label: t('navigation.models'), icon: 'cube' },
    { href: '/bookmarks', label: t('navigation.bookmarks'), icon: 'bookmark', requiresAuth: true },
    ...(!hasUser ? [
      { href: '/explore', label: t('navigation.explore'), icon: 'compass' },
      { href: '/prompts', label: t('navigation.prompts'), icon: 'layer-group' },
      { href: '/images', label: t('navigation.createImages'), icon: 'image' },
      { href: '/videos', label: t('navigation.createVideos'), icon: 'video' },
      { href: '/music', label: t('navigation.createMusic'), icon: 'music' },
    ] : []),
  ];
  const isActive = href => href === '/app'
    ? /^\/(app|c|g|m|collections|library)(\/|$)/.test(pathname || '')
    : pathname === href || pathname?.startsWith(`${href}/`);
  const didNavigate = () => {
    onNavigate?.();
    if (Platform.OS !== 'macos') navigation?.closeDrawer();
  };
  const openDestination = item => {
    const navigate = () => router.push(item.href);
    if (item.requiresAuth) {
      requireAuth(navigate, {
        source: 'navigation_bookmarks',
        title: 'Sign in to save your favorites',
        subtitle: 'Keep useful responses and creations in one place, ready when you need them.',
        icon: 'bookmark',
      });
    } else {
      navigate();
    }
    didNavigate();
  };
  return (
    <View accessibilityLabel="Main navigation" accessibilityRole={Platform.OS === 'web' ? 'navigation' : undefined}
      style={[styles.rail, { borderRightColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
      {header}
      <ScrollView style={styles.scroll} contentContainerStyle={styles.destinations} showsVerticalScrollIndicator={false}>
        {primary.map(item => <RailDestination key={item.href} {...item} active={isActive(item.href)} colors={colors} onPress={didNavigate} />)}
        <DropdownMenu.Root>
          <Tooltip label={t('navigation.more')} side="right">
            <DropdownMenu.Trigger asChild>
              <MoreTrigger accessibilityRole="button" tabIndex={0} accessibilityLabel={t('navigation.more')}
                style={StyleSheet.flatten([styles.button, more.some(item => isActive(item.href)) && { backgroundColor: colors.solid_color_secondary }])}>
                <Icon name="ellipsis" size={21} color={colors.text_color_secondary} />
              </MoreTrigger>
            </DropdownMenu.Trigger>
          </Tooltip>
          <DropdownMenu.Content className="DropdownMenuContent" side="right" align="start" sideOffset={8}>
            {more.map(item => <DropdownMenu.Item className="DropdownMenuItem" key={item.href} onSelect={() => openDestination(item)}>
              <DropdownMenu.ItemIcon className="DropdownMenuItemIcon"
                ios={{ name: MORE_NATIVE_ICONS[item.icon][0] }} androidIconName={MORE_NATIVE_ICONS[item.icon][1]}>
                <Icon name={item.icon} size={18} color={colors.text_color_primary} />
              </DropdownMenu.ItemIcon>
              <DropdownMenu.ItemTitle className="DropdownMenuItemTitle">{item.label}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>)}
          </DropdownMenu.Content>
        </DropdownMenu.Root>
        {children}
      </ScrollView>
      <View style={styles.footer}>
        {hasUser && !isGhost && <RailCreditBalance colors={colors} onPress={didNavigate} />}
        <RailDestination label={t('sidebar.help')} icon="circle-question" href={hasUser ? '/settings?tab=help' : '/docs/quickstart'} colors={colors} onPress={didNavigate} />
        {hasUser ? <SidebarAccountButton navigation={navigation} compact /> : (
          <RailDestination label={t('sidebar.logIn')} icon="circle-user" colors={colors}
            onPress={() => { openModal('LoginModal'); didNavigate(); }} />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  rail: { width: ARTIFACT_NAV_RAIL_WIDTH, flexShrink: 0, height: '100%', borderRightWidth: 1, minHeight: 0 },
  scroll: { flex: 1, minHeight: 0 },
  destinations: { alignItems: 'center', gap: 8, paddingTop: 8, paddingBottom: 16 },
  button: { width: 44, height: 44, alignSelf: 'center', alignItems: 'center', justifyContent: 'center', borderRadius: 12 },
  creditButton: { width: 52, minHeight: 44, alignSelf: 'center', alignItems: 'center', justifyContent: 'center', borderRadius: 12, paddingHorizontal: 2, paddingVertical: 4, marginBottom: 4 },
  creditAmount: { fontVariant: ['tabular-nums'], maxWidth: '100%' },
  footer: { alignItems: 'center', paddingTop: 8, paddingBottom: 4 },
});
